import { addDays, formatPence, wallClock } from '@tikkas/shared';
/** The London calendar day, 'YYYY-MM-DD'. Tomorrow and yesterday are calendar days, never ±24 hours (the clocks change). */
const dayKey = (x: Date): string => wallClock(x).dateKey;
export const fmt = (p: number): string => formatPence(Math.max(0, Math.round(p)));
/** £9.95 -> "£9.95", £3.00 -> "£3" (for hints). */
export const fmtS = (p: number): string => (p % 100 ? formatPence(p) : `£${p / 100}`);
export const num = (n: number): string => Math.round(n).toLocaleString('en-GB');
export const hhmmLocal = (iso: string): string => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
export const relDay = (iso: string): string => {
  const d = new Date(iso), today = dayKey(new Date());
  if (dayKey(d) === today) return 'Today';
  if (dayKey(d) === addDays(today, -1)) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/London' });
};
export const whenLabel = (iso: string): string => `${relDay(iso)} ${hhmmLocal(iso)}`;
/*
 * When an order is ready, for a sentence ("Ready …", "ready …"): "about 17:45" the same day, "tomorrow at 12:20",
 * else "Mon 19 Oct, 12:20". Apple reviews at any hour and the review notes say to pick the next opening, so a
 * collection a day away must read as a day and time, never as hundreds of minutes.
 */
export const readyWhen = (iso: string, now: Date = new Date()): string => {
  const d = new Date(iso), today = dayKey(now);
  if (dayKey(d) === today) return `about ${hhmmLocal(iso)}`;
  if (dayKey(d) === addDays(today, 1)) return `tomorrow at ${hhmmLocal(iso)}`;
  return `${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/London' })}, ${hhmmLocal(iso)}`;
};
/** The live row's time, short: "14:32" today, "tomorrow 14:32", else "Thu 23 Oct 14:32" (the day only when it isn't today; Saif, 22 Oct). */
export const readyShort = (iso: string, now: Date = new Date()): string => {
  const d = new Date(iso), today = dayKey(now);
  if (dayKey(d) === today) return hhmmLocal(iso);
  if (dayKey(d) === addDays(today, 1)) return `tomorrow ${hhmmLocal(iso)}`;
  return `${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/London' })} ${hhmmLocal(iso)}`;
};
/** The order screen's countdown line: minutes only for a short wait the same day, otherwise the day and time. */
export const readyCountdown = (iso: string, now: Date = new Date()): string => {
  const w = readyWhen(iso, now);
  if (!w.startsWith('about ')) return w.charAt(0).toUpperCase() + w.slice(1);
  const mins = Math.round((new Date(iso).getTime() - now.getTime()) / 60000);
  if (mins > 120) return `Today at ${hhmmLocal(iso)}`;
  return mins > 1 ? `About ${mins} min to go` : 'Any moment now';
};
export const uid = (): string => Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-3);
/**
 * An unguessable key (128 bits) for a checkout attempt. The server replays the same order for the same key, and for a guest
 * the replay carries the order's link token, so the key must not be guessable the way uid() is.
 */
export const secureKey = (): string => {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
};
export const reduced = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};
export const vibrate = (p: number | number[]): void => {
  try {
    navigator.vibrate?.(p);
  } catch {
    /* no haptics */
  }
};
